import { FC, useContext, useEffect, useRef, useState } from "react";

import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";

import { ToastTypes } from "src/types";
import { APIEndpoints } from "src/types/apiTypes";
import { UploadClinicalInfoProps } from "./types";
import { DocumentAPIResponse } from "../documentUpload/types";
import { LoaderContext } from "src/context/loaderContext";
import { ACCEPTED_PRECERT_FILE_EXTENSIONS, MAX_FILE_SIZE_IN_BYTES } from "src/constants";



const UploadClinicalInformation: FC<UploadClinicalInfoProps> = ({ refreshCall, caseId }): JSX.Element => {
  const [uploadFile, changeUploadFile] = useState<FileList | null>(null);
  const [paValue, changePAValue] = useState<boolean>(true);
  const [uploadFileInputError, changeUploadFileInputError] = useState<string | null>(null);

  const ref = useRef<HTMLInputElement>(null);
  const { showToast } = useContext(ToastContext);
  const { hideLoader } = useContext(LoaderContext);

  const reset = () => {
    changeUploadFile(null);
    if (ref && ref.current) {
      ref.current.value = ""
    }
  };

  const clearDataAndForm = () => {
    changeUploadFile(null);
    reset();
  };





  const { loading: uploadFileLoading, error: uploadFileError, data: uploadFileResponse, mutate: uploadCaseDocument } =
    useFetch<DocumentAPIResponse>(APIEndpoints.UPLOAD_CASE_FILE);

  useEffect(() => {
    if (uploadFileResponse || uploadFileError) {
      hideLoader();
      if(uploadFileError) {
        changeUploadFile(null);
        reset();
      }
      if(uploadFileResponse) {
        // const { message } = uploadFileResponse;
        showToast(["case updated"], ToastTypes.SUCCESS);
        reset();
        refreshCall && refreshCall()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileResponse, uploadFileError]);

  const completeOnMyBehalf = () => {
    if (uploadFile) {
      const formData = new FormData();
      formData.append('file', uploadFile[0]);
      formData.append("type", "clinicals");
      uploadCaseDocument(formData, { caseId });
    }
  };


  const onUplaodCalled = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const caseFileExt = `.${files[0].name.split(".").pop()}`;
      const isExtAccepted = Object.keys(ACCEPTED_PRECERT_FILE_EXTENSIONS).find((ext) => caseFileExt === ext);
      if (!isExtAccepted) {
        changeUploadFileInputError(`Not an acceptable format`);
      } else if (files[0].size > MAX_FILE_SIZE_IN_BYTES) {
        changeUploadFileInputError("File size exceeds 25MB limit");
      } else {
        changeUploadFileInputError(null);
        changeUploadFile(files)
      }
    }
  }

  return (
    <div className="card">
      <div className="card-header bg-success-subtle">
        <h4 className="mb-0 text-black">
          Upload Clinical Information
        </h4>
      </div>

      <div className="card-body patient-info-details">
        <p>
          My QUTENZA Connect can help by processing potential precertification or prior authorization
          requests on your behalf. For assistance, fax (855.454.8746) or upload relevant supporting
          clinical chart documentation related to the patient's indication. In particular ensure the 
          following elements are included:
        </p>
        <div className="row">
          <div className="col-12 col-lg-4"></div>
          <div className="col-12 col-lg-4">
            <ul className="fw-bold">
              <li>Visit notes within last 12 months</li>
              <li>List all tried and failed and/or did not tolerate medications</li>
              <li>The ICD-10/diagnosis code must be in the clinical notes</li>
              <li>The provider listed on the case must match provider in clinical notes</li>
            </ul>
          </div>
          <div className="col-12 col-lg-4"></div>
        </div>

        <p>
          If approved, the prior authorization will be sent to you and will be forwarded to your
          pre-selected specialty pharmacy (if you have chosen to use a specialty pharmacy in the
          QUTENZA network):
        </p>

        <form>
          <div className="form-check">
            <input className="form-check-input" type="radio" name="pa" id="paYes" checked={paValue} onChange={(e) => changePAValue(true)} />
            <label className="form-check-label fw-bold" htmlFor="paYes">
              Complete precertification or prior authorization on behalf of my office (fax or upload chart 
              documentation below)
            </label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="radio" name="pa" id="paNo" checked={!paValue} onChange={(e) => changePAValue(false)} />
            <label className="form-check-label fw-bold" htmlFor="paNo">
              Do not complete on behalf of my office (click “skip for now” below)
            </label>
          </div>

          <div className="card mt-2">
            <div className="card-header bg-success-subtle">
              <h4 className="text-black mb-0">
                <i className="bi bi-cloud-arrow-up-fill"></i>{' '}
                Upload Patient Chart
              </h4>
            </div>

            <div className="card-body">
              <div className="input-group mb-2">
                <input 
                  type="file" id="fileUpload" className="d-none" ref={ref}
                  onChange={onUplaodCalled}
                />
                <input
                  type="text" className="form-control bg-secondary-subtle" id="fileUpload"
                  value={uploadFile && uploadFile.length > 0 ? uploadFile[0].name : ""}
                />
                <div className="">
                  {!uploadFile ? (
                    <button type="button" onClick={() => ref.current ? ref.current.click() : null} className="btn btn-light rounded-end-0">Choose a file</button>
                  ) : null}
                  {uploadFile ? (
                    <>
                      <button
                        className="btn btn-outline-secondary me-2" onClick={clearDataAndForm} type="button"
                        id="fileUpload" disabled={uploadFileLoading}
                      >
                        Clear
                      </button>
                      <button 
                        type="button" onClick={() => ref.current ? ref.current.click() : null} 
                        className="btn btn-light rounded-end-0"
                      >
                        Change
                      </button>
                    </>
                  ) : null}
                  <button
                    className="btn btn-blue rounded-start-0"
                    type="button"
                    onClick={completeOnMyBehalf}
                    disabled={!uploadFile || uploadFileLoading}
                  >
                    {uploadFileLoading ? (
                      <i
                        className="spinner-border me-2"
                        style={{ width: "16px", height: "16px", fontSize: "9px" }}
                      ></i>
                    ) : null}
                    Upload
                  </button>
                </div>
              </div>
              {uploadFileInputError && <p className="d-block text-danger mb-0">{uploadFileInputError}</p>}
              <small className="d-block" style={{fontSize:'16px'}}>
                Click here to upload the patient's chart. Currently we accept .txt, .pdf, .doc, .tiff, .docx, .ccd, .cda, .ccr, .ccda and image files.
              </small>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UploadClinicalInformation
